import { inject, Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppUpdateService,
    awaitAppConfigSettled,
    BaseInitializationService,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConcurrentDataSyncError,
    ConfigInstanceToken,
    ConfigInterface,
    ConfigRequestsInstanceToken,
    ConfigRequestsInterface,
    ConnectivityService,
    CseStockService,
    DateManipulationCheckInstanceToken,
    DateManipulationCheckInterface,
    DeviceLockManagementModalComponent,
    DeviceLockService,
    EncryptionInitializer,
    EncryptionMigrator,
    EventService,
    FileStorageInjectionToken,
    FileStorageServiceInterface,
    INIT_MESSAGE_KEY,
    INIT_MESSAGE_STATE,
    InitError,
    InitMessage,
    InputFieldPropertiesInstanceToken,
    loadAppConfig,
    loadUserStore,
    LocalStorageAppStateService,
    LocalStorageCommonService,
    MaterialService,
    MeasurementToolsInstanceToken,
    MeasurementToolsInterface,
    ModalClass,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationStatusInstanceToken,
    NotificationUpdateService,
    PwaPushNotificationInstanceToken,
    PwaPushNotificationInterface,
    RecipientsInstanceToken,
    RecipientsInterface,
    SideMenuInstanceToken,
    SideMenuInterface,
    SynchronizationService,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
    UriUtilitiesService,
    UserGidService,
    UserMasterInstanceToken,
    UserMasterInterface,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    WeeklyWorkTimeInstanceToken,
    WeeklyWorkTimeInterface,
} from '@evorait/pace-core';
import { FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { PhysicalInventoryService } from '../../components/header/physical-inventory/services/physical-inventory.service';
import { SmartlockerService } from '../../../smartlocker/services/smartlocker.service';

@Injectable({
    providedIn: 'root',
})
export class InitializationService extends BaseInitializationService {
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);
    private readonly inputFieldPropertiesService = inject(InputFieldPropertiesInstanceToken);
    private readonly store = inject(Store);

    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(SideMenuInstanceToken) protected readonly sideMenuService: SideMenuInterface,
        @Inject(ConfigRequestsInstanceToken) protected readonly configInterfaceService: ConfigRequestsInterface,
        @Inject(FileStorageInjectionToken) protected readonly fileStorageServiceInterface: FileStorageServiceInterface,
        protected readonly modalService: BsModalService,
        protected readonly localStorageAppStateService: LocalStorageAppStateService,
        protected readonly connectivityService: ConnectivityService,
        protected readonly uriUtilities: UriUtilitiesService,
        @Inject(UserMasterInstanceToken) protected readonly userMasterService: UserMasterInterface,
        protected readonly appNotificationService: AppNotificationService,
        protected readonly translate: TranslateService,
        protected readonly eventService: EventService,
        protected readonly synchronizationService: SynchronizationService,
        protected readonly offlineService: OfflineService,
        protected readonly userGidService: UserGidService,
        @Inject(WeeklyWorkTimeInstanceToken) protected readonly weeklyWorkTimeService: WeeklyWorkTimeInterface,
        @Inject(ConfigInstanceToken) protected readonly configService: ConfigInterface,
        protected readonly localStorageCommonService: LocalStorageCommonService,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly cseStockService: CseStockService,
        protected readonly encryptionInitializer: EncryptionInitializer,
        @Inject(RecipientsInstanceToken) private readonly recipientsService: RecipientsInterface,
        @Inject(MeasurementToolsInstanceToken) private readonly measurementToolsService: MeasurementToolsInterface,
        private readonly appUpdateService: AppUpdateService,
        private readonly formsService: FormsService,
        public readonly modalInstance: BsModalRef,
        @Inject(PwaPushNotificationInstanceToken)
        protected readonly pwaPushNotificationService: PwaPushNotificationInterface,
        @Inject(DateManipulationCheckInstanceToken)
        protected readonly dateManipulationCheckService: DateManipulationCheckInterface,
        protected readonly materialService: MaterialService,
        protected readonly encryptionMigrator: EncryptionMigrator,
        private readonly deviceLockService: DeviceLockService,
        private readonly physicalInventoryService: PhysicalInventoryService,
        private readonly smartlockerService: SmartlockerService,
    ) {
        super(
            log,
            clientManager,
            sideMenuService,
            configInterfaceService,
            modalService,
            localStorageAppStateService,
            connectivityService,
            uriUtilities,
            userMasterService,
            appNotificationService,
            translate,
            eventService,
            synchronizationService,
            configService,
            offlineService,
            userGidService,
            localStorageCommonService,
            fileStorageServiceInterface,
            encryptionMigrator,
            encryptionInitializer,
        );
    }

    protected setupInitProcessPipe(): void {
        this.init$ = of(true);
        // PWA update must run before the device lock check: AppUpdateService.init() only
        // subscribes to SwUpdate events locally and makes no backend call, so it doesn't need
        // the lock — and running it first means a device that is locked out still gets offered
        // app updates instead of being stuck on a stale version.
        this.init$ = this.attachPWAAppUpdate(this.init$);
        // The device lock must be acquired before any other backend call: since the v1 API
        // every feature endpoint sits behind deviceLockMiddleware and answers 423 while
        // another device holds the lock.
        this.init$ = this.attachDeviceLockCheck(this.init$);
        this.init$ = this.attachEncryptionServiceInit(this.init$).pipe(switchMap(() => this.attachRehydration()));
        this.init$ = this.upgradeLegacyEncryption(this.init$);
        // The concurrent-usage download stores data encrypted, so it must run after the
        // encryption init — while the lock itself is acquired first (attachDeviceLockCheck).
        this.init$ = this.attachConcurrentDataSync(this.init$);
        this.init$ = this.attachInternetConnectionCheck(this.init$);
        this.init$ = this.attachDateManipulationCheckServiceInit(this.init$);
        this.init$ = this.attachUserMasterServiceInit(this.init$);
        this.init$ = this.attachPWAPushNotifications(this.init$);
        this.init$ = this.attachAppConfigInit(this.init$);
        // This next line has been deactivated here, but enabled on base-initialization service.
        // Main reason is, that attachConfigInterfaceInit() should come after ...
        // attachServiceInit(), because its initializing the ConfigSet things.
        // And for attachConfigInterfaceInit() we need these config parameters to be there,
        // to build up the NdPHeaderSet parameters (with or without Source Replacement, Paperwork)
        // Thats why things are shifted now. The exact order of init things is still not thought through anymore
        // and should be revised.
        // this.init$ = this.attachConfigInterfaceInit(this.init$);
        // this.init$ = this.attachVersionAndSessionSyncWithGateway(this.init$);
        // this.init$ = this.attachSessionCheck(this.init$);
        this.init$ = this.attachInitDurationLogger(this.init$);
        this.init$ = this.attachAppInitStateChange(this.init$);
    }

    public init(): Observable<void> {
        return super.init().pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    this.log.warn(
                        'received an initialization error, but it can be handled and we will just continue',
                        error,
                    );
                    return of(undefined);
                }
                this.log.warn('received an initialization error, that can not be handled', error);
                return throwError(() => error);
            }),
            tap(() => {
                this.pwaPushNotificationService.askUserForPushNotificationPermission();
            }),
        );
    }

    /**
     * Acquires the device lock as the very first init step. If the lock is held by another
     * device, the fullscreen lock management screen is shown so the user can inspect the
     * holder and force-unlock.
     *
     * @param obs$
     */
    private attachDeviceLockCheck(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.DEVICE_LOCK_CHECK,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_LOCK_CHECK'),
                of('0s'),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            mergeMap(() => {
                startTimer = moment();
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DEVICE_LOCK_CHECK,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_LOCK_CHECK'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return this.deviceLockService.init();
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DEVICE_LOCK_CHECK,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_LOCK_CHECK_SUCCESS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                this.log.debug(
                    `InitializationService: attachDeviceLockCheck: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DEVICE_LOCK_CHECK,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_INFO_LOCK_CHECK_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (error instanceof ConcurrentDataSyncError) {
                    // the lock itself is owned by this device — showing the lock screen would
                    // be misleading; the progress modal surfaces the sync failure with retry
                    return throwError(() => new InitError(error));
                }

                this.modalService.show(DeviceLockManagementModalComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_FULLSCREEN,
                    ariaLabelledBy: 'app-notification-modal-title',
                    ignoreBackdropClick: true,
                    keyboard: false,
                });
                return throwError(() => new InitError(<Error>error));
            }),
        );
    }

    /**
     * Downloads the concurrent-usage data belonging to the current lock acquisition.
     * Runs as its own step after the encryption init because the download stores data
     * encrypted; a failure surfaces in the progress modal, never as a lock problem.
     *
     * @param obs$
     */
    private attachConcurrentDataSync(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.CONCURRENT_DATA,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_CONCURRENT_SYNC'),
                of('0s'),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            mergeMap(() => {
                startTimer = moment();
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONCURRENT_DATA,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_CONCURRENT_SYNC'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return this.deviceLockService.ensureConcurrentDataSynced();
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONCURRENT_DATA,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_CONCURRENT_SYNC_SUCCESS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.CONCURRENT_DATA,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_INFO_CONCURRENT_SYNC_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
        );
    }

    /**
     * Make sure the odata consuming services are initialized in the correct order
     *
     * @param obs$ the observable to attach the initialization to
     * @returns the observable with the initialization attached
     */
    public attachServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SERVICE_INIT,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream(`APP_START_INIT_OTHER_SERVICES`),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$
            .pipe(
                catchError((error) => {
                    if (this.errorCanBeHandled(error)) {
                        previousError = <InitError>error;
                        return of(undefined);
                    }

                    return throwError(() => error);
                }),
                mergeMap(() => {
                    startTimer = moment();

                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream('APP_START_INIT_CONFIG_VALUEHELPER_INPUTFIELDPROPERTIES'),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return forkJoin([
                        this.configService.init(),
                        this.valueHelperService.init(),
                        this.inputFieldPropertiesService.init(),
                    ]);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.log.warn(
                        `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return of(undefined);
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream('APP_START_INIT_CONFIG_VALUEHELPER_INPUTFIELDPROPERTIES'),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );

                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_TIMESHEET_AND_EXPENSES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.timesheetAndExpensesService.init();
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_TIMESHEET_AND_EXPENSES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_WEEKLY_WORK_TIME`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.weeklyWorkTimeService.init();
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_WEEKLY_WORK_TIME`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION_STATUS`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.notificationStatusService.init();
                }),
            )
            .pipe(
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION_STATUS`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.notificationService.init(true);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.log.warn(
                        `InitializationService: attachServiceInit: Error while initializing services: `,
                        error,
                    );
                    return of(undefined);
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    const initServices = [
                        this.notificationUpdateService.init(),
                        this.materialService.init(),
                        this.cseStockService.init(),
                        this.measurementToolsService.init(),
                        this.recipientsService.init(),
                    ];

                    if (this.configService.getEnablePhysicalInventory()) {
                        initServices.push(this.physicalInventoryService.init());
                    }

                    if (this.configService.getEnableSmartlocker()) {
                        initServices.push(this.smartlockerService.init());
                    }
                    return forkJoin(initServices);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.ERROR,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS_ERROR`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.log.error(
                        `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return throwError(() => new InitError(<Error>error, true));
                }),
                map(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_OTHER_SERVICES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.log.debug(
                        `InitializationService: attachServiceInit: time taken: ${
                            moment().valueOf() - startTimer.valueOf()
                        } ms`,
                    );
                    return undefined;
                }),
                mergeMap((result) => {
                    if (typeof previousError !== 'undefined') {
                        return throwError(() => previousError);
                    }
                    return of(result);
                }),
            );
    }

    // Function to load any effects that should happen after initialization is completed.
    public executeRemainingSideEffects(): void {
        this.formsService.restoreLastOpenedForm();
    }

    /**
     * Initialize Encryption service
     *
     * @param obs$ the observable to attach the initialization to
     * @returns the observable with the initialization attached
     */
    private attachEncryptionServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_ENCRYPTION'),
                of('0s'),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.encryptionInitializer.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            tap(() => {
                this.log.debug(
                    `InitializationService: attachEncryptionServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_ENCRYPTION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
        );
    }

    private attachDateManipulationCheckServiceInit(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = error;
                    return of(undefined);
                }
                startTimer = moment();
                return throwError(() => error);
            }),
            tap(() => {
                startTimer = moment();
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            mergeMap(() => this.dateManipulationCheckService.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (previousError) {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_DATE_MANIPULATION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() => {
                this.log.debug(
                    `InitializationService: attachDateManipulationCheckServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
        );
    }

    private attachPWAAppUpdate(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.appUpdateService.init()),
            map(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return true;
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachAppUpdate: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }

    public attachPWAPushNotifications(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.pwaPushNotificationService.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_PWA_PUSH_NOTIFICATIONS_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachPwaPushNotificationsService: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }

    public attachAppConfigInit(obs$: Observable<unknown>): Observable<unknown> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.APP_CONFIG,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_APP_CONFIG'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }
                startTimer = moment();
                return throwError(() => error);
            }),
            tap(() => {
                startTimer = moment();
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.APP_CONFIG,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_APP_CONFIG'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            mergeMap(() => {
                this.store.dispatch(loadAppConfig());
                // The user store is loaded here too, alongside app config, so its mirror is warm
                // by the time any page (location-tracker among them) needs it. Deliberately not
                // awaited like app config is below — app start must not block on it settling.
                this.store.dispatch(loadUserStore());
                return awaitAppConfigSettled(this.store);
            }),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.APP_CONFIG,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_APP_CONFIG'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (previousError) {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.APP_CONFIG,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_APP_CONFIG_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachAppConfigInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }
}
